// What the ledger says happened to your covert ops, and to you.
//
// The sim publishes each seat's side of every resolved op (`globals.espionage
// [seat].log`) and decides what that seat may know. This only turns the entries
// it has not shown yet into words and a sound; it decides nothing.

import type { GameSnapshot } from '@aod/protocol';
import type { MessageKey } from '../i18n/index.js';

export type OpKind = 'sabotage' | 'assassination' | 'poison' | 'intercept';
export type OpRole = 'culprit' | 'victim' | 'framed';
export type OpOutcome = 'success' | 'detected' | 'agent_lost' | 'unnoticed';

/** One resolved op as this seat is allowed to see it. */
export interface OpLogEntry {
  readonly id: number;
  readonly tick: number;
  readonly kind: OpKind;
  readonly role: OpRole;
  readonly outcome: OpOutcome;
  /** Who this seat believes did it (or, for the culprit, who they framed). */
  readonly blamed: number | null;
  /** The true culprit, where this seat is entitled to know. */
  readonly culprit: number | null;
}

/**
 * The ledger this seat may read, with the houses it names filled in as
 * `null`.
 *
 * The wasm wire sends an absent `Option` as `undefined` (serde_wasm_bindgen
 * serialises `None` as `undefined`, not `null`), so a line whose culprit or
 * blame is unknown read as `undefined` where the type says `null` — and every
 * `=== null` check on it failed: the news line named "Player NaN" and the
 * message read "blamed" where it should read "unknown". The multiplayer JSON
 * path sends real `null`s, so only the wasm client saw it.
 */
export function readOpLog(snapshot: GameSnapshot | null, seat: number): readonly OpLogEntry[] {
  const globals = snapshot?.state.globals as
    | { espionage?: Record<string, { log?: OpLogEntry[] }> }
    | undefined;
  return (globals?.espionage?.[String(seat)]?.log ?? []).map((e) => ({
    ...e,
    blamed: e.blamed ?? null,
    culprit: e.culprit ?? null,
  }));
}

/** The entries newer than `lastSeen`, oldest first. */
export function freshOps(log: readonly OpLogEntry[], lastSeen: number): readonly OpLogEntry[] {
  return log.filter((e) => e.id > lastSeen);
}

/** The message key for an entry; the house in it is `opOther`. */
export function opMessageKey(e: OpLogEntry): MessageKey {
  const base = 'espionage.news';
  if (e.role === 'framed') return `${base}.framed.${e.kind}` as MessageKey;
  if (e.role === 'victim') {
    const how =
      e.outcome === 'success'
        ? e.blamed === null && e.culprit === null
          ? 'unknown'
          : 'blamed'
        : 'caught';
    return `${base}.theirs.${e.kind}.${e.kind === 'intercept' ? 'caught' : how}` as MessageKey;
  }
  const how = { success: 'success', detected: 'seen', agent_lost: 'lost', unnoticed: 'quiet' }[
    e.outcome
  ];
  return `${base}.mine.${e.kind}.${how}` as MessageKey;
}

/** The house a line names: who you blame, or who struck you. */
export function opOther(e: OpLogEntry): number | null {
  return e.culprit ?? e.blamed;
}

/** Tone of a line, for colour: good for you, bad for you, or neutral. */
export function opTone(e: OpLogEntry): 'good' | 'bad' | 'neutral' {
  if (e.role === 'victim') return e.outcome === 'unnoticed' ? 'neutral' : 'bad';
  if (e.role === 'framed') return 'bad';
  // Catching an agent is the defence's win; the other ops win by succeeding.
  if (e.kind === 'intercept') return e.outcome === 'agent_lost' ? 'good' : 'bad';
  return e.outcome === 'success' ? 'good' : 'bad';
}

/** The registered sound for an entry, if it has one. */
export function opSound(e: OpLogEntry): string | null {
  if (e.role !== 'culprit' || e.kind === 'intercept') return null;
  if (e.kind === 'assassination')
    return e.outcome === 'success' ? 'assassin_success' : 'assassin_failure';
  if (e.kind === 'sabotage' && e.outcome === 'success') return 'sabotage_planted';
  return e.outcome === 'success' ? null : 'assassin_failure';
}
